import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Papa, { ParseResult } from 'papaparse';

// Import UI components
import { Button } from "@/components/ui/button";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Import select components
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

// Import API and types
import { api } from "@/lib/api";
import {
  VerifiedPhoneNumber,
  StrategyWithVersions,
} from "@/lib/types";

const BulkCallFormSchema = z.object({
  verified_phone_number_id: z.string().min(1, "Please select a verified phone number"),
  strategy_id: z.string().min(1, "Please select a strategy"),
  strategy_version_id: z.string().min(1, "Please select a strategy version"),
});

type BulkCallFormValues = z.infer<typeof BulkCallFormSchema>;

function BulkCallForm({
  organizationId,
  onSuccess,
}: {
  organizationId: string;
  onSuccess?: () => void;
}) {
  const [verifiedPhoneNumbers, setVerifiedPhoneNumbers] = useState<VerifiedPhoneNumber[]>([]);
  const [strategies, setStrategies] = useState<StrategyWithVersions[]>([]);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Load verified phone numbers and strategies when component mounts
  useEffect(() => {
    Promise.all([
      api.getVerifiedPhoneNumbers(organizationId),
      api.getOrganizationStrategies(organizationId)
    ]).then(([phones, strats]) => {
      setVerifiedPhoneNumbers(phones);
      setStrategies(strats);
    }).catch((error) => {
      console.error("Failed to fetch data:", error);
      toast({
        title: "Failed to load data",
        description: String(error),
        variant: "destructive",
      });
    });
  }, [organizationId]);

  const form = useForm<BulkCallFormValues>({
    resolver: zodResolver(BulkCallFormSchema),
    defaultValues: {
      verified_phone_number_id: "",
      strategy_id: "",
      strategy_version_id: "",
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result;
      Papa.parse(csv as string, {
        header: true,
        complete: (results: ParseResult<any>) => {
          console.log('Parsed CSV data:', results.data);
          setCsvData(results.data);
          setIsUploading(false);
        },
        error: (error: Error, file: File) => {
          console.error('Error parsing CSV:', error);
          setIsUploading(false);
          toast({
            title: "Failed to parse CSV",
            description: String(error),
            variant: "destructive",
          });
        }
      });
    };
    reader.readAsText(file);
  };

  const watchStrategyId = form.watch("strategy_id");
  useEffect(() => {
    form.setValue("strategy_version_id", "");
  }, [watchStrategyId]);

  const onSubmit = async (data: BulkCallFormValues) => {
    if (csvData.length === 0) {
      toast({
        title: "No CSV data",
        description: "Please upload a CSV file first",
        variant: "destructive",
      });
      return;
    }

    try {
      // Transform CSV data into CallCreateList format
      const calls = csvData.map((row) => {
        const entries = Object.entries(row);
        const [_, phoneNumber] = entries[0]; // First column is phone number
        const additionalInfo = Object.fromEntries(entries.slice(1)); // Rest of columns become call_specific_info

        return {
          callee_phone_number: phoneNumber as string,
          strategy_version_id: data.strategy_version_id,
          verified_phone_number_id: data.verified_phone_number_id,
          call_specific_info: Object.keys(additionalInfo).length > 0 ? additionalInfo : undefined,
        };
      });

      const response = await api.createBulkCalls({ calls });
      
      toast({ 
        title: "Success", 
        description: `Created ${response.calls.length} calls` 
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to create bulk calls:", error);
      toast({
        title: "Failed to create bulk calls",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <DialogHeader className="mb-6">
        <DialogTitle>Bulk Create Calls</DialogTitle>
        <DialogDescription>
          Upload a CSV file and select common parameters for bulk call creation.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormItem className="flex flex-col gap-2">
            <FormLabel className="text-left">CSV File</FormLabel>
            <FormControl>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="cursor-pointer block w-full"
              />
            </FormControl>
            {isUploading && <p>Uploading...</p>}
            {csvData.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {csvData.length} rows parsed from CSV
              </p>
            )}
          </FormItem>

          <FormItem>
            <FormLabel>Verified Phone Number</FormLabel>
            <FormControl>
              <Select
                value={form.watch("verified_phone_number_id")}
                onValueChange={(value) => form.setValue("verified_phone_number_id", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a verified phone number" />
                </SelectTrigger>
                <SelectContent>
                  {verifiedPhoneNumbers.map((phoneNumber) => (
                    <SelectItem key={phoneNumber.id} value={phoneNumber.id}>
                      {phoneNumber.phone_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>Strategy</FormLabel>
            <FormControl>
              <Select
                value={form.watch("strategy_id")}
                onValueChange={(value) => form.setValue("strategy_id", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a strategy" />
                </SelectTrigger>
                <SelectContent>
                  {strategies.map((strategy) => (
                    <SelectItem key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>

          {form.watch("strategy_id") && (
            <FormItem>
              <FormLabel>Strategy Version</FormLabel>
              <FormControl>
                <Select
                  value={form.watch("strategy_version_id")}
                  onValueChange={(value) => form.setValue("strategy_version_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a strategy version" />
                  </SelectTrigger>
                  <SelectContent>
                    {strategies
                      .find((s) => s.id === form.watch("strategy_id"))
                      ?.versions.map((version) => (
                        <SelectItem key={version.id} value={version.id}>
                          {version.version_description || `Version ${version.id}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}

          <DialogFooter>
            <Button type="submit" disabled={csvData.length === 0}>
              Create Bulk Calls
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}

export default BulkCallForm;